import { getConfigurationLineTotal } from 'helpers/DealQuantity';
/**
 * Whether to show quantity for an option/package row on the public quote.
 * In BUNDLED mode, included configurations keep an empty quantity cell.
 */
export function showBundledQuoteConfigurationQuantity(configuration, pricingDisplay, editable) {
    if (editable) {
        return true;
    }
    if (pricingDisplay !== 'BUNDLED') {
        return true;
    }
    return !configuration.included;
}
/**
 * Per-line extension amounts for bundled quote display.
 * Aligns option sums with QuoteSummary (feeValue ?? price).
 */
export function getDealItemBundledExtensionParts(item, deal) {
    var _a, _b, _c, _d, _e, _f, _g;
    const itemBaseExtension = ((_a = item.price) !== null && _a !== void 0 ? _a : 0) * ((_b = item.quantity) !== null && _b !== void 0 ? _b : 0);
    const packagesExtension = (_d = (_c = item.packages) === null || _c === void 0 ? void 0 : _c.reduce((acc, curr) => {
        var _a, _b, _c;
        return acc +
            (!curr.proposed
                ? getConfigurationLineTotal((_a = curr.price) !== null && _a !== void 0 ? _a : 0, (_b = curr.quantity) !== null && _b !== void 0 ? _b : 0, (_c = curr.package) === null || _c === void 0 ? void 0 : _c.quantityCalculation, deal)
                : 0);
    }, 0)) !== null && _d !== void 0 ? _d : 0;
    const optionsExtension = (_f = (_e = item.options) === null || _e === void 0 ? void 0 : _e.reduce((acc, curr) => {
        var _a, _b, _c, _d;
        return acc +
            (!curr.proposed
                ? getConfigurationLineTotal((_b = ((_a = curr.feeValue) !== null && _a !== void 0 ? _a : curr.price)) !== null && _b !== void 0 ? _b : 0, (_c = curr.quantity) !== null && _c !== void 0 ? _c : 0, (_d = curr.item) === null || _d === void 0 ? void 0 : _d.quantityCalculation, deal)
                : 0);
    }, 0)) !== null && _f !== void 0 ? _f : 0;
    const bundledExtension = itemBaseExtension + packagesExtension + optionsExtension;
    const q = (_g = item.quantity) !== null && _g !== void 0 ? _g : 1;
    const bundledUnitPrice = q > 0 ? bundledExtension / q : 0;
    return {
        itemBaseExtension,
        packagesExtension,
        optionsExtension,
        bundledExtension,
        bundledUnitPrice
    };
}
/**
 * Whether a dealItem can participate in same-saleItem bundling.
 */
export function isDealItemBundlable(item, allItems) {
    if (item.proposed) {
        return false;
    }
    if (item.alternativeTo) {
        return false;
    }
    if (allItems.some((i) => i.alternativeTo === item.id)) {
        return false;
    }
    return true;
}
/**
 * Identifier used to dedupe included configurations in bundled display.
 */
function getConfigurationCatalogKey(configuration, key) {
    var _a, _b, _c, _d;
    if (key === 'options') {
        return (_b = (_a = configuration.item) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
    }
    return (_d = (_c = configuration.package) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : null;
}
/**
 * Merge options or packages from multiple dealItems.
 * Included configurations sharing the same catalog item keep only the first occurrence.
 */
export function mergeDealItemConfigurations(items, key) {
    const merged = items.flatMap((item) => { var _a; return (_a = item[key]) !== null && _a !== void 0 ? _a : []; });
    const seenIncludedKeys = new Set();
    return merged.filter((configuration) => {
        if (!configuration.included) {
            return true;
        }
        const catalogKey = getConfigurationCatalogKey(configuration, key);
        if (!catalogKey) {
            return true;
        }
        if (seenIncludedKeys.has(catalogKey)) {
            return false;
        }
        seenIncludedKeys.add(catalogKey);
        return true;
    });
}
/**
 * Sum bundled extensions for all dealItems in a group.
 */
export function getBundledGroupExtensionTotal(items, deal) {
    return items.reduce((acc, item) => acc + getDealItemBundledExtensionParts(item, deal).bundledExtension, 0);
}
/**
 * Build ordered display entries for bundled quote summary.
 * Eligible dealItems sharing the same saleItem are grouped at the first occurrence.
 */
export function buildQuoteBundledDisplayEntries(items) {
    var _a;
    const bundlableBySaleItemId = new Map();
    for (const item of items) {
        if (!isDealItemBundlable(item, items)) {
            continue;
        }
        const saleItemId = item.item.id;
        const group = bundlableBySaleItemId.get(saleItemId);
        if (group) {
            group.push(item);
        }
        else {
            bundlableBySaleItemId.set(saleItemId, [item]);
        }
    }
    const consumedIds = new Set();
    const entries = [];
    for (const item of items) {
        if (consumedIds.has(item.id)) {
            continue;
        }
        if (isDealItemBundlable(item, items)) {
            const group = (_a = bundlableBySaleItemId.get(item.item.id)) !== null && _a !== void 0 ? _a : [item];
            if (group.length > 1) {
                group.forEach((groupItem) => consumedIds.add(groupItem.id));
                entries.push({
                    kind: 'group',
                    saleItemId: item.item.id,
                    items: group
                });
                continue;
            }
        }
        entries.push({ kind: 'single', item });
    }
    return entries;
}
